import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { routes } from 'routes'
import { logSingleAction } from 'utils/helperMethods'
import { router } from '../../core/Root'
import * as emailActions from '../email/slice'
import * as folderActions from '../folder/slice'
import * as api from './api'

// Action type constants
const GET_ALL = 'folderHistory/GET_ALL'
const GET_APPROVER_INFO = 'folderHistory/GET_APPROVER_INFO'
const APPROVE_FOR_CONTROL = 'folderHistory/APPROVE_FOR_CONTROL'
const APPROVE_FOR_CONTROL_AND_ASSIGN_TO = 'folderHistory/APPROVE_FOR_CONTROL_AND_ASSIGN_TO'
const APPROVE_FOR_SENDING = 'folderHistory/APPROVE_FOR_SENDING'
const APPROVE_FOR_SENDING_AND_ASSIGN_TO = 'folderHistory/APPROVE_FOR_SENDING_AND_ASSIGN_TO'
const APPROVE_AND_REQUEST_ADDITIONAL_CONTROL = 'folderHistory/APPROVE_AND_REQUEST_ADDITIONAL_CONTROL'
const REJECT_CONTROL = 'folderHistory/REJECT_CONTROL'
const REJECT_CONTROL_AND_ASSIGN_TO = 'folderHistory/REJECT_CONTROL_AND_ASSIGN_TO'
const REQUEST_AUDIT = 'folderHistory/REQUEST_AUDIT'
const REQUEST_AUDIT_AND_ASSIGN_TO = 'folderHistory/REQUEST_AUDIT_AND_ASSIGN_TO'
const REJECT_SEND = 'folderHistory/REJECT_SEND'
const REJECT_SEND_AND_ASSIGN_TO = 'folderHistory/REJECT_SEND_AND_ASSIGN_TO'
const COMPLETE_VAT_AUDIT = 'folderHistory/COMPLETE_VAT_AUDIT'
const COMPLETE_AUDIT_AND_ASSIGN_TO = 'folderHistory/COMPLETE_AUDIT_AND_ASSIGN_TO'
const REJECT_VAT_AUDIT = 'folderHistory/REJECT_VAT_AUDIT'
const REJECT_VAT_AUDIT_AND_ASSIGN_TO = 'folderHistory/REJECT_VAT_AUDIT_AND_ASSIGN_TO'
const COMPLETE_AUDIT = 'folderHistory/COMPLETE_AUDIT'
const REJECT_AUDIT = 'folderHistory/REJECT_AUDIT'
const REJECT_AUDIT_AND_ASSING_TO = 'folderHistory/REJECT_AUDIT_AND_ASSING_TO'
const REJECT_AUDITED = 'folderHistory/REJECT_AUDITED'
const REJECT_AUDITED_AND_ASSIGN_TO = 'folderHistory/REJECT_AUDITED_AND_ASSIGN_TO'
const REJECT_SENT = 'folderHistory/REJECT_SENT'
const REJECT_SENT_AND_ASSIGN_TO = 'folderHistory/REJECT_SENT_AND_ASSIGN_TO'
const ASSIGN_FOR_APPROVAL = 'folderHistory/ASSIGN_FOR_APPROVAL'
const DISPATCH_TO_FLOW = 'folderHistory/DISPATCH_TO_FLOW'
const CLIENT_REJECT = 'folderHistory/CLIENT_REJECT'

const customOnFulfilled = async (folderId, thunkAPI, data) => {
  const state = thunkAPI.getState()
  const createTime = state.folder.model?.createTime
  thunkAPI.dispatch(folderActions.get({ id: folderId, createTime }))
  thunkAPI.dispatch(getAll({ id: folderId, createTime }))
  thunkAPI.fulfillWithValue(data)
  thunkAPI.dispatch(getApproverInfo({ id: folderId, createTime }))
  const basePath = routes.folderDetails.path.replace(':id', folderId)
  const pathToNavigate = createTime 
    ? `${basePath}?createTime=${encodeURIComponent(createTime)}` 
    : basePath
  router.navigate(pathToNavigate, { state: { ...router?.state?.location?.state, createTime } })
}

export const getAll = createAsyncThunk(GET_ALL, async ({ id, createTime }, thunkAPI) => {
  const { data, error } = await api.getAll(id, createTime)
  if (error) return thunkAPI.rejectWithValue(error)
  else {
    return data
  }
})

export const getApproverInfo = createAsyncThunk(GET_APPROVER_INFO, async ({ id, createTime }, thunkAPI) => {
  const { data, error } = await api.getApproverInfo(id, createTime)
  if (error) {
    return thunkAPI.rejectWithValue(error)
  } else {
    return data
  }
})

export const approveForControl = createAsyncThunk(APPROVE_FOR_CONTROL, async (values, thunkAPI) => {
  const state = thunkAPI.getState()

  logSingleAction(APPROVE_FOR_CONTROL, state)
  const { data, error } = await api.approveForControl({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, APPROVE_FOR_CONTROL)
})

export const approveForControlAndAssignTo = createAsyncThunk(
  APPROVE_FOR_CONTROL_AND_ASSIGN_TO,
  async (values, thunkAPI) => {
    const state = thunkAPI.getState()
    logSingleAction(APPROVE_FOR_CONTROL_AND_ASSIGN_TO, state)

    const { data, error } = await api.approveForControlAndAssignTo({
      ...values,
      actionToken: state.folder.actionToken,
    })
    if (error) return thunkAPI.rejectWithValue(error)
    customOnFulfilled(values.folderId, thunkAPI, data, APPROVE_FOR_CONTROL_AND_ASSIGN_TO)
  },
)

export const approveForSending = createAsyncThunk(APPROVE_FOR_SENDING, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(APPROVE_FOR_SENDING, state)
  const { data, error } = await api.approveForSending({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, APPROVE_FOR_SENDING)
})

export const approveForSendingAndAssignTo = createAsyncThunk(
  APPROVE_FOR_SENDING_AND_ASSIGN_TO,
  async (values, thunkAPI) => {
    const state = thunkAPI.getState()
    logSingleAction(APPROVE_FOR_SENDING_AND_ASSIGN_TO, state)

    const { data, error } = await api.approveForSendingAndAssignTo({
      ...values,
      actionToken: state.folder.actionToken,
    })
    if (error) return thunkAPI.rejectWithValue(error)
    customOnFulfilled(values.folderId, thunkAPI, data, APPROVE_FOR_SENDING_AND_ASSIGN_TO)
  },
)

export const approveAndRequestAdditionalApproval = createAsyncThunk(
  APPROVE_AND_REQUEST_ADDITIONAL_CONTROL,
  async (values, thunkAPI) => {
    const state = thunkAPI.getState()
    logSingleAction(APPROVE_AND_REQUEST_ADDITIONAL_CONTROL, state)

    const { data, error } = await api.approveAndRequestAdditionalApproval({
      ...values,
      actionToken: state.folder.actionToken,
    })
    if (error) return thunkAPI.rejectWithValue(error)
    customOnFulfilled(values.folderId, thunkAPI, data, APPROVE_AND_REQUEST_ADDITIONAL_CONTROL)
  },
)

export const rejectControl = createAsyncThunk(REJECT_CONTROL, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_CONTROL, state)
  const { data, error } = await api.rejectControl({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_CONTROL)
})

export const rejectControlAndAssignTo = createAsyncThunk(REJECT_CONTROL_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_CONTROL_AND_ASSIGN_TO, state)

  const { data, error } = await api.rejectControlAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_CONTROL_AND_ASSIGN_TO)
})

export const requestAudit = createAsyncThunk(REQUEST_AUDIT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REQUEST_AUDIT, state)

  const { data, error } = await api.requestAudit({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REQUEST_AUDIT)
})

export const requestAuditAndAssignTo = createAsyncThunk(REQUEST_AUDIT_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REQUEST_AUDIT_AND_ASSIGN_TO, state)

  const { data, error } = await api.requestAuditAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REQUEST_AUDIT_AND_ASSIGN_TO)
})

export const rejectSend = createAsyncThunk(REJECT_SEND, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_SEND, state)
  const { data, error } = await api.rejectSend({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_SEND)
})

export const rejectSendAndAssignTo = createAsyncThunk(REJECT_SEND_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_SEND_AND_ASSIGN_TO, state)
  const { data, error } = await api.rejectSendAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_SEND_AND_ASSIGN_TO)
})

export const completeVatAudit = createAsyncThunk(COMPLETE_VAT_AUDIT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(COMPLETE_VAT_AUDIT, state)
  const { data, error } = await api.completeVatAudit({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, COMPLETE_VAT_AUDIT)
})

export const completeVatAuditAndAssignTo = createAsyncThunk(COMPLETE_AUDIT_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(COMPLETE_AUDIT_AND_ASSIGN_TO, state)
  const { data, error } = await api.completeVatAuditAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, COMPLETE_AUDIT_AND_ASSIGN_TO)
})

export const rejectVatAudit = createAsyncThunk(REJECT_VAT_AUDIT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_VAT_AUDIT, state)
  const { data, error } = await api.rejectVatAudit({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_VAT_AUDIT)
})

export const rejectVatAuditAndAssignTo = createAsyncThunk(REJECT_VAT_AUDIT_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_VAT_AUDIT_AND_ASSIGN_TO, state)
  const { data, error } = await api.rejectVatAuditAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_VAT_AUDIT_AND_ASSIGN_TO)
})

export const completeAudit = createAsyncThunk(COMPLETE_AUDIT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(COMPLETE_AUDIT, state)
  const { data, error } = await api.completeAudit({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, COMPLETE_AUDIT)
})

export const rejectAudit = createAsyncThunk(REJECT_AUDIT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_AUDIT, state)
  const { data, error } = await api.rejectAudit({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_AUDIT)
})

export const rejectAuditAndAssignTo = createAsyncThunk(REJECT_AUDIT_AND_ASSING_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_AUDIT_AND_ASSING_TO, state)
  const { data, error } = await api.rejectAuditAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_AUDIT_AND_ASSING_TO)
})

export const rejectAudited = createAsyncThunk(REJECT_AUDITED, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_AUDITED, state)
  const { data, error } = await api.rejectAudited({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_AUDITED)
})

export const rejectAuditedAndAssignTo = createAsyncThunk(REJECT_AUDITED_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_AUDITED_AND_ASSIGN_TO, state)
  const { data, error } = await api.rejectAuditedAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_AUDITED_AND_ASSIGN_TO)
})

export const rejectSent = createAsyncThunk(REJECT_SENT, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_SENT, state)
  const { data, error } = await api.rejectSent({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_SENT)
})

export const rejectSentAndAssignTo = createAsyncThunk(REJECT_SENT_AND_ASSIGN_TO, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(REJECT_SENT_AND_ASSIGN_TO, state)
  const { data, error } = await api.rejectSentAndAssignTo({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, REJECT_SENT_AND_ASSIGN_TO)
})

export const assignForApproval = createAsyncThunk(ASSIGN_FOR_APPROVAL, async (values, thunkAPI) => {
  const state = thunkAPI.getState()
  logSingleAction(ASSIGN_FOR_APPROVAL, state)
  const { data, error } = await api.assignForApproval({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  customOnFulfilled(values.folderId, thunkAPI, data, ASSIGN_FOR_APPROVAL)
})

export const dispatchToFlow = createAsyncThunk(DISPATCH_TO_FLOW, async ({ values, tableState }, thunkAPI) => {
  const state = thunkAPI.getState()
  const { data, error } = await api.dispatchToFlow({
    ...values,
    actionToken: state.folder.actionToken,
  })
  if (error) return thunkAPI.rejectWithValue(error)
  else {
    thunkAPI.fulfillWithValue(data)
    if (!tableState) {
      const createTime = state.folder.model?.createTime
      thunkAPI.dispatch(folderActions.get({ id: values.folderId, createTime }))
      thunkAPI.dispatch(getApproverInfo({ id: values.folderId, createTime }))
      const basePath = routes.folderDetails.path.replace(':id', values.folderId)
      const pathToNavigate = createTime 
        ? `${basePath}?createTime=${encodeURIComponent(createTime)}` 
        : basePath
      router.navigate(pathToNavigate, { state: { ...router?.state?.location?.state, createTime } })
    } else {
      thunkAPI.dispatch(folderActions.getAll(tableState))
    }
  }
})

export const clientReject = createAsyncThunk(CLIENT_REJECT, async (values, thunkAPI) => {
  const { error } = await api.clientReject(values)
  if (error) return thunkAPI.rejectWithValue(error)
  else {
    thunkAPI.dispatch(emailActions.getEmailInfo(values.emailGuid))
  }
})

export const slice = createSlice({
  name: 'folderHistory',
  initialState: {
    list: [],
    isListLoading: false,
    error: null,
    approvalsError: null,
    approverInfoModel: null,
    isApproverInfoModelLoading: false,
  },
  reducers: {
    clearError: state => {
      state.error = null
    },
    updateSelections: (state, action) => {
      const { rowId, isSelect, statusId, activeFlowId, sefStatus } = action.payload

      function updateSelected(rowId, isSelect) {
        return isSelect
          ? [...state.selections, { rowId, statusId, activeFlowId, sefStatus }]
          : state.selections.filter(item => item !== rowId)
      }
      state.selections = updateSelected(rowId, isSelect)
    },
    cleareErrorsAndSelection: state => {
      state.EFacturaError = null
      state.selections = []
      state.cancelResult = null
    },
  },
  extraReducers: builder => [
    builder
      .addCase(getAll.pending, state => {
        state.isListLoading = true
        state.error = null
      })
      .addCase(getAll.fulfilled, (state, action) => {
        state.isListLoading = false
        state.list = action.payload
      })
      .addCase(getAll.rejected, (state, action) => {
        state.isModelLoading = false
        state.error = action.error
      })

      .addCase(getApproverInfo.pending, state => {
        state.isApproverInfoModelLoading = true
      })
      .addCase(getApproverInfo.fulfilled, (state, { payload }) => {
        state.approverInfoModel = payload
        state.isApproverInfoModelLoading = false
      })
      .addCase(getApproverInfo.rejected, (state, { error }) => {
        state.approvalsError = error
        state.isApproverInfoModelLoading = false
      })

      .addCase(approveForControl.pending, state => {
        state.error = null
        state.approvalsError = null
      })
      .addCase(approveForControl.fulfilled, () => {})
      .addCase(approveForControl.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(approveForControlAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(approveForControlAndAssignTo.fulfilled, () => {})
      .addCase(approveForControlAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(approveForSending.pending, state => {
        state.error = null
      })
      .addCase(approveForSending.fulfilled, () => {})
      .addCase(approveForSending.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(approveForSendingAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(approveForSendingAndAssignTo.fulfilled, () => {})
      .addCase(approveForSendingAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(approveAndRequestAdditionalApproval.pending, state => {
        state.error = null
      })
      .addCase(approveAndRequestAdditionalApproval.fulfilled, () => {})
      .addCase(approveAndRequestAdditionalApproval.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectControl.pending, state => {
        state.error = null
      })
      .addCase(rejectControl.fulfilled, () => {})
      .addCase(rejectControl.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectControlAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectControlAndAssignTo.fulfilled, () => {})
      .addCase(rejectControlAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(requestAudit.pending, state => {
        state.error = null
      })
      .addCase(requestAudit.fulfilled, () => {})
      .addCase(requestAudit.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(requestAuditAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(requestAuditAndAssignTo.fulfilled, () => {})
      .addCase(requestAuditAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectSendAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectSendAndAssignTo.fulfilled, () => {})
      .addCase(rejectSendAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(completeVatAudit.pending, state => {
        state.error = null
      })
      .addCase(completeVatAudit.fulfilled, () => {})
      .addCase(completeVatAudit.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(completeVatAuditAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(completeVatAuditAndAssignTo.fulfilled, () => {})
      .addCase(completeVatAuditAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectVatAudit.pending, state => {
        state.error = null
      })
      .addCase(rejectVatAudit.fulfilled, () => {})
      .addCase(rejectVatAudit.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectVatAuditAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectVatAuditAndAssignTo.fulfilled, () => {})
      .addCase(rejectVatAuditAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(completeAudit.pending, state => {
        state.error = null
      })
      .addCase(completeAudit.fulfilled, () => {})
      .addCase(completeAudit.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectAudit.pending, state => {
        state.error = null
      })
      .addCase(rejectAudit.fulfilled, () => {})
      .addCase(rejectAudit.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectAuditAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectAuditAndAssignTo.fulfilled, () => {})
      .addCase(rejectAuditAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectAudited.pending, state => {
        state.error = null
      })
      .addCase(rejectAudited.fulfilled, () => {})
      .addCase(rejectAudited.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectAuditedAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectAuditedAndAssignTo.fulfilled, () => {})
      .addCase(rejectAuditedAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectSent.pending, state => {
        state.error = null
      })
      .addCase(rejectSent.fulfilled, () => {})
      .addCase(rejectSent.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectSentAndAssignTo.pending, state => {
        state.error = null
      })
      .addCase(rejectSentAndAssignTo.fulfilled, () => {})
      .addCase(rejectSentAndAssignTo.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(rejectSend.pending, state => {
        state.error = null
      })
      .addCase(rejectSend.fulfilled, () => {})
      .addCase(rejectSend.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(dispatchToFlow.pending, state => {
        state.error = null
      })
      .addCase(dispatchToFlow.fulfilled, () => {})
      .addCase(dispatchToFlow.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(assignForApproval.pending, state => {
        state.error = null
      })
      .addCase(assignForApproval.fulfilled, () => {})
      .addCase(assignForApproval.rejected, (state, { error }) => {
        state.error = error
      })

      .addCase(clientReject.pending, state => {
        state.error = null
      })
      .addCase(clientReject.fulfilled, () => {})
      .addCase(clientReject.rejected, (state, { error }) => {
        state.error = error
      }),
  ],
})

export const { clearError, updateSelections, cleareErrorsAndSelection } = slice.actions

export const listSelector = state => state.folderHistory.list
export const isListLoadingSelector = state => state.folderHistory.isListLoading
export const errorSelector = state => state.folderHistory.error
export const approvalsErrorSelector = state => state.folderHistory.approvalsError
export const approverInfoModelSelector = state => state.folderHistory.approverInfoModel
export const isApproverInfoLoadingSelector = state => state.folderHistory.isApproverInfoModelLoading

export const reducer = slice.reducer
